import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/Layout";
import { ScrollText, Search, Filter, RefreshCw, ChevronDown, ChevronUp, User, Globe, Loader2 } from "lucide-react";

/**
 * Admin Audit Log page.
 * Reads from GET /api/audit with optional filters: action prefix, user_email,
 * entity, free-form q. Defaults to last 100 rows, capped at 500.
 */
export default function AuditPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState({
    action: "",
    user_email: "",
    entity: "",
    q: "",
    limit: 100,
  });
  const [openId, setOpenId] = useState(null); // expanded row payload

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const params = new URLSearchParams();
      if (filter.action)     params.set("action", filter.action);
      if (filter.user_email) params.set("user_email", filter.user_email);
      if (filter.entity)     params.set("entity", filter.entity);
      if (filter.q)          params.set("q", filter.q);
      params.set("limit", String(filter.limit));
      const res = await api.get(`/audit?${params.toString()}`);
      setRows(res.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Audit log yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, []); // initial load only

  const onSubmit = (e) => { e.preventDefault(); load(); };

  // Distinct chips for quick action filters from current data
  const actionPrefixes = useMemo(() => {
    const set = new Set();
    rows.forEach(r => {
      const prefix = (r.action || "").split(".")[0];
      if (prefix) set.add(prefix);
    });
    return Array.from(set).sort();
  }, [rows]);

  return (
    <AdminLayout>
      <div className="p-6 space-y-5" data-testid="audit-page">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <ScrollText className="text-amber-500" /> Yönetici İşlem Günlüğü
          </h1>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 hover:border-amber-500/50 text-white text-sm transition disabled:opacity-50"
            data-testid="audit-refresh"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Yenile
          </button>
        </div>

        {/* Filter bar */}
        <form onSubmit={onSubmit} className="bg-slate-900 border border-slate-800 rounded-xl p-4 grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="audit-filters">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1 block">Arama</label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="action / e-posta / entity_id"
                value={filter.q}
                onChange={(e) => setFilter({ ...filter, q: e.target.value })}
                className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-9 pr-3 py-2 text-white text-sm focus:border-amber-500 outline-none"
                data-testid="audit-search-q"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1 block">Action prefix</label>
            <input
              type="text"
              placeholder="ör: customer.cancel"
              value={filter.action}
              onChange={(e) => setFilter({ ...filter, action: e.target.value })}
              className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm font-mono focus:border-amber-500 outline-none"
              data-testid="audit-filter-action"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1 block">E-posta</label>
            <input
              type="email"
              placeholder="admin@..."
              value={filter.user_email}
              onChange={(e) => setFilter({ ...filter, user_email: e.target.value })}
              className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 outline-none"
              data-testid="audit-filter-email"
            />
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={loading}
              className="w-full h-9 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold flex items-center justify-center gap-2 disabled:opacity-50 transition"
              data-testid="audit-apply-filter"
            >
              <Filter size={14} /> Filtrele
            </button>
          </div>
        </form>

        {/* Quick chips */}
        {actionPrefixes.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="audit-chips">
            <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500 self-center mr-1">Hızlı:</span>
            {actionPrefixes.map(p => (
              <button
                key={p}
                onClick={() => { setFilter(f => ({ ...f, action: p })); setTimeout(load, 0); }}
                className={`px-3 py-1 rounded-full text-[11px] font-mono border transition ${
                  filter.action === p
                    ? "bg-amber-500/15 border-amber-500/40 text-amber-300"
                    : "bg-slate-900 border-slate-800 text-gray-400 hover:border-amber-500/30 hover:text-white"
                }`}
                data-testid={`audit-chip-${p}`}
              >
                {p}.*
              </button>
            ))}
            {filter.action && (
              <button
                onClick={() => { setFilter(f => ({ ...f, action: "" })); setTimeout(load, 0); }}
                className="px-3 py-1 rounded-full text-[11px] font-mono border bg-slate-900 border-slate-800 text-gray-500 hover:text-white"
                data-testid="audit-chip-clear"
              >
                ✕ tümü
              </button>
            )}
          </div>
        )}

        {/* Table */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full text-left text-white" data-testid="audit-table">
            <thead className="bg-slate-950 text-gray-400 text-xs uppercase font-bold tracking-wider">
              <tr>
                <th className="p-3 w-44">Tarih (UTC)</th>
                <th className="p-3">Admin</th>
                <th className="p-3">İşlem</th>
                <th className="p-3">Entity</th>
                <th className="p-3 hidden md:table-cell">IP</th>
                <th className="p-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr><td colSpan={6} className="p-12 text-center text-gray-500">
                  <Loader2 className="inline w-5 h-5 animate-spin" />
                </td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="p-12 text-center text-gray-500 italic" data-testid="audit-empty">
                  {err || "Bu filtreyle eşleşen kayıt yok."}
                </td></tr>
              ) : (
                rows.map(r => {
                  const isOpen = openId === r._id;
                  return (
                    <>
                      <tr
                        key={r._id}
                        className="hover:bg-slate-800/30 transition cursor-pointer"
                        onClick={() => setOpenId(isOpen ? null : r._id)}
                        data-testid={`audit-row-${r._id}`}
                      >
                        <td className="p-3 text-xs font-mono text-gray-400">{(r.created_at || "").replace("T", " ").slice(0, 19)}</td>
                        <td className="p-3 text-sm">
                          <div className="inline-flex items-center gap-1.5">
                            <User size={12} className="text-amber-400" />
                            <span className="text-white">{r.user_email || "—"}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="font-mono text-[12px] text-amber-400">{r.action}</span>
                          {r.entity_id && (
                            <span className="ml-2 text-[10px] font-mono text-gray-500 truncate" title={r.entity_id}>
                              #{String(r.entity_id).slice(0, 8)}
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-sm text-gray-400">{r.entity || "—"}</td>
                        <td className="p-3 hidden md:table-cell text-xs font-mono text-gray-500">
                          {r.ip ? (
                            <span className="inline-flex items-center gap-1">
                              <Globe size={10} /> {r.ip}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="p-3 text-gray-500">
                          {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr key={r._id + ":payload"} className="bg-slate-950/60" data-testid={`audit-row-payload-${r._id}`}>
                          <td colSpan={6} className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                              <Detail label="User-Agent" value={r.user_agent} mono full />
                              <Detail label="Entity ID" value={r.entity_id} mono />
                              <Detail label="IP" value={r.ip} mono />
                              <div className="md:col-span-3">
                                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Payload</div>
                                <pre className="bg-slate-900 border border-slate-800 rounded p-3 text-[11px] text-gray-300 font-mono overflow-auto max-h-40">
{JSON.stringify(r.payload || {}, null, 2)}
                                </pre>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })
              )}
            </tbody>
          </table>
          <div className="px-4 py-3 border-t border-slate-800 text-[11px] text-gray-500 flex items-center justify-between">
            <span>Toplam {rows.length} kayıt {rows.length === filter.limit && `(limit: ${filter.limit} — daha fazlası için filtre uygulayın)`}</span>
            <div className="flex items-center gap-2">
              Limit:
              <select
                value={filter.limit}
                onChange={(e) => { setFilter(f => ({ ...f, limit: parseInt(e.target.value) })); setTimeout(load, 0); }}
                className="bg-slate-950 border border-slate-800 rounded px-2 py-1 text-white text-xs"
                data-testid="audit-limit"
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
                <option value={500}>500</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}


function Detail({ label, value, mono = false, full = false }) {
  if (!value && value !== 0) return null;
  return (
    <div className={full ? "md:col-span-3" : ""}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">{label}</div>
      <div className={`text-gray-300 break-all ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
